import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';

import { useGeolocationState } from '@/contexts/GeolocationContext';
import { GeolocationProvider } from '@/contexts/GeolocationContext';
import { Carousel } from '@/design-system/atoms/Carousel';
import { ContentContainer } from '@/design-system/atoms/ContentContainer';
import { Theme } from '@/design-system/atoms/Theme';
import { Title } from '@/design-system/atoms/Title';
import { LocationDetail } from '@/design-system/molecules/LocationDetail';
import { useLocalStorageGeolocation } from '@/hooks/useLocalStorageGeolocation';

import styles from './ProviderLocations.module.scss';

const ProviderLocationsComponent = ({ title, htmlId, locations, isInEditor, ...props }) => {
    const [localStorageGeolocation] = useLocalStorageGeolocation();
    const { zipCode, city } = useGeolocationState() ?? {};
    const [distances, setDistances] = useState({});
    const NUMBER_OF_DECIMALS = 2;

    /**
     * Get the distance between the user's location and a given address
     * @param {string} lat - The user's latitude
     * @param {string} lng - The user's longitude
     * @param {string} locationId - The destination locationId to calculate the distance to
     * @param {AbortSignal} signal - The AbortSignal to cancel the request
     * @returns {Promise<string | null>} - The distance in miles or null if an error occurred
     */
    const getDistance = useCallback(
        async (lat, lng, locationId, signal = null) => {
            if (!locationId || !lat || !lng) {
                return null;
            }

            const response = await fetch(
                `/bin/ahs/fadlocdistance?lat=${lat}&long=${lng}&locId=${locationId}`,
                { signal },
            );

            if (!response.ok) {
                throw new Error(`Error: Status ${response.status},${response.message}`);
            }

            const { distanceInMiles } = await response.json();
            return distanceInMiles
                ? `${distanceInMiles?.toFixed(NUMBER_OF_DECIMALS)} miles away from ${
                      city || zipCode
                  }`
                : null;
        },
        [city, zipCode],
    );

    useEffect(() => {
        const controller = new AbortController();
        const calculateDistances = async () => {
            const distancePromises = locations.map(async (location) => {
                const { locationId } = location;
                if (!locationId) {
                    return { [locationId]: null };
                }

                try {
                    const distanceText = await getDistance(
                        localStorageGeolocation?.lat,
                        localStorageGeolocation?.lng,
                        locationId,
                        controller.signal,
                    );
                    return { [locationId]: distanceText };
                } catch (error) {
                    console.error('Error calculating distance:', error);
                    return { [locationId]: null };
                }
            });
            const resolvedDistances = await Promise.all(distancePromises);
            const distanceMap = resolvedDistances.reduce((acc, curr) => ({ ...acc, ...curr }), {});

            setDistances(distanceMap);
        };

        if (localStorageGeolocation?.lat && localStorageGeolocation?.lng) {
            calculateDistances();
        }

        return () => controller.abort();
    }, [locations, localStorageGeolocation, getDistance]);

    // Create a list of LocationDetail components with distance information
    const items = locations.map((location, index) => {
        const { locationId } = location;
        const distanceText = distances[locationId] || null;

        const {
            locationType,
            className,
            title: locationTitle,
            parentLocationName,
            rootLocationName,
            buildingName,
            address,
            phoneNumber,
            workingHours,
            temporarilyClosed,
            overrideMessage,
            cta,
            analytics,
            waitTime,
            epicDepartmentId,
        } = location;

        const analyticsObj = {
            ...analytics,
            componentName: 'fad_bio_locations',
            componentTitle: title,
        };

        return (
            <Carousel.Card
                fullHeight
                key={`${htmlId}-${index}`}
                className={styles[`provider-locations__card`]}
            >
                <LocationDetail
                    locationType={locationType}
                    className={className}
                    title={locationTitle}
                    parentLocationName={parentLocationName}
                    rootLocationName={rootLocationName}
                    buildingName={buildingName}
                    address={address}
                    phoneNumber={phoneNumber}
                    workingHours={workingHours}
                    temporarilyClosed={temporarilyClosed}
                    overrideMessage={overrideMessage}
                    cta={cta}
                    analytics={analyticsObj}
                    waitTime={waitTime}
                    epicDepartmentId={epicDepartmentId}
                    variant={LocationDetail.VARIANT.PROVIDER_LOCATION}
                    distance={distanceText}
                />
            </Carousel.Card>
        );
    });
    if (items.length === 0 && !isInEditor) {
        return null;
    }

    return (
        <section className={styles[`provider-locations`]} {...props} id={htmlId}>
            <ContentContainer>
                <div className={styles['provider-locations__header']}>
                    {title && (
                        <Title
                            tag={Title.TAG.H2}
                            variant={Title.VARIANT.H3}
                            content={title}
                            lineClamp={{ sm: 2, lg: 1 }}
                            className={styles['provider-locations__title']}
                        />
                    )}
                </div>
            </ContentContainer>
            <Carousel.Root>
                <Carousel.Content items={items} />
                <Carousel.Footer>
                    <div />
                    <Carousel.Spacer />
                    <Carousel.Navigation
                        analytics={{
                            componentName: 'fad_bio_locations',
                            componentTitle: title,
                        }}
                    />
                </Carousel.Footer>
            </Carousel.Root>
        </section>
    );
};

const ProviderLocations = ({ ...props }) => {
    return (
        <GeolocationProvider>
            <ProviderLocationsComponent {...props} />
        </GeolocationProvider>
    );
};

ProviderLocations.defaultProps = {
    htmlId: 'locations',
    title: 'Where to see me',
    locations: [],
};

ProviderLocations.propTypes = {
    theme: PropTypes.oneOf(Object.values(Theme.NAME)),
    htmlId: PropTypes.string,
    title: PropTypes.string,
    locations: PropTypes.array,
};

export default ProviderLocations;
